import type {
  CustomerTimelineOptedOutEntry,
  CustomerTimelineOutreachFinishedEntry,
  CustomerTimelineTaskCanceledEntry,
  CustomerTimelineTaskCreatedEntry,
  CustomerTimelineTaskFailedEntry,
} from "@repo/app-types/entities/performanceCustomer";
import type { CustomerTimelineRow } from "@repo/ui/customerTimeline/customerTimeline";
import { CustomerTimelineActivityDetails } from "@repo/ui/customerTimeline/customerTimelineActivityDetails";
import { CustomerTimelineDotNode } from "@repo/ui/customerTimeline/customerTimelineNodes";
import { callFailureReasonLabel } from "../../teammatePerformance/callActivityColumns.js";
import {
  buildEntryRowId,
  formatCustomerTimelineTime,
} from "./timelineRowHelpers.js";

/** Entries drawn as a single activity row. */
export type CustomerTimelineActivityEntry =
  | CustomerTimelineTaskCreatedEntry
  | CustomerTimelineTaskCanceledEntry
  | CustomerTimelineTaskFailedEntry
  | CustomerTimelineOutreachFinishedEntry
  | CustomerTimelineOptedOutEntry;

export function buildActivityEntryRow(
  entry: CustomerTimelineActivityEntry,
): CustomerTimelineRow {
  return {
    id: buildEntryRowId(entry),
    dateTime: entry.at,
    dateLabel: formatCustomerTimelineTime(entry.at),
    node: <CustomerTimelineDotNode />,
    details: (
      <CustomerTimelineActivityDetails>
        {buildActivityEntryLabel(entry)}
      </CustomerTimelineActivityDetails>
    ),
  };
}

function buildActivityEntryLabel(entry: CustomerTimelineActivityEntry): string {
  switch (entry.type) {
    case "task_created":
      return entry.is_agent_managed ? "Outreach started" : "Call requested";
    case "task_canceled":
      return entry.is_agent_managed ? "Outreach stopped" : "Call canceled";
    case "task_failed": {
      const label = entry.is_agent_managed ? "Outreach failed" : "Call failed";
      const reason = callFailureReasonLabel[entry.error_code];
      return reason ? `${label} — ${reason}` : label;
    }
    case "outreach_finished":
      return "Outreach ended";
    case "opted_out":
      return "Customer opted out";
  }
}
